import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../../../contexts/AuthContext';
import {
  useFormSubmissionSettings,
} from '../../../contexts/FormSubmissionSettingsContext';
import { useFieldMetadata } from '../../../contexts/FieldMetadataContext';
import { useOrganization2 } from '../../../contexts/OrganizationContext2';
import useFormIntegrationConfigs from '../../../hooks/useFormIntegrationConfigs';
import {
  fetchIntegrationStatus,
} from '../../Integrations/integrationFunctions';
import {
  BITLY_CLICKS_TOTAL_COLUMN_ID,
  BITLY_SHORT_URL_COLUMN_ID,
  buildBitlyClicksTotalColumn,
  buildBitlyShortUrlColumn,
} from '../../../integrations/providers/bitly/bitlyProviderColumns';
import {
  hideProviderColumn,
  upsertProviderColumn,
} from '../../../utils/providerOutputColumnUtils';

const ADMIN_ROLES = new Set([
  'owner',
  'admin',
  'superAdmin',
  'super_app_admin',
  'superAppadmin',
]);

const DISPLAY_CARD_ACTION = 'displayInCard';
const DISPLAY_TABLE_ACTION = 'addToSubmissionsTable';

function getCallableErrorMessage(error) {
  const code = error?.code || '';
  const message = error?.message || '';

  if (code.includes('permission-denied')) {
    return 'You do not have permission to view this integration.';
  }

  if (code.includes('unauthenticated')) {
    return 'Please sign in again before viewing integrations.';
  }

  return message || 'Unable to load Bitly integration status.';
}

function asStringOrNull(value) {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed || null;
}

function getFieldTypeLabel(type) {
  if (type === 'calculated') return 'Calculated';
  if (type === 'urlField') return 'URL field';
  return type || 'Field';
}

function getNormalizedFieldsMetadata(fieldsMetadata = {}) {
  const normalized = fieldsMetadata?.value || fieldsMetadata || {};

  if (
    !normalized ||
    typeof normalized !== 'object' ||
    Array.isArray(normalized)
  ) {
    return {};
  }

  return normalized;
}

function buildEligibleDisplayTableDestinationFields(fieldsMetadata = {}) {
  const normalized = getNormalizedFieldsMetadata(fieldsMetadata);

  return Object.entries(normalized)
    .map(([fieldId, meta = {}]) => {
      const ufi = meta?.ufi || fieldId;
      const type = meta?.type || '';

      return {
        fieldId: meta?.id || fieldId,
        ufi,
        type,
        label: meta?.label || meta?.name || fieldId,
      };
    })
    .filter((field) => {
      return (
        field.ufi &&
        (
          field.type === 'calculated' ||
          field.type === 'urlField'
        )
      );
    })
    .sort((a, b) => {
      const aRank = a.type === 'calculated' ? 0 : 1;
      const bRank = b.type === 'calculated' ? 0 : 1;

      if (aRank !== bRank) return aRank - bRank;

      return String(a.label || '').localeCompare(String(b.label || ''));
    });
}

function buildBitlyFormConfig({
  enabled,
  actionType,
  destinationUfi = null,
}) {
  const isDisplayTableAction = actionType === DISPLAY_TABLE_ACTION;

  return {
    provider: 'bitly',
    enabled,
    outputAction: actionType,
    destinationUrlSource: isDisplayTableAction
      ? 'responseUfi'
      : 'displayInCardMessage',
    destinationUfi: isDisplayTableAction
      ? asStringOrNull(destinationUfi)
      : null,
    destinationFieldKey: null,
    fallbackMode: 'return_long_url_if_shortlink_fails',
    domain: 'bit.ly',
    groupGuid: null,
  };
}

export default function BitlyShortlinkSettingsSection({
  actionType = DISPLAY_CARD_ACTION,
  generatedOutputLabel = 'Display Card generated URL',
}) {
  const {
    hasPermission,
    userProfile,
    userRole,
  } = useAuth();

  const { organization } = useOrganization2();

  const {
    fieldsMetadata,
  } = useFieldMetadata() || {};

  const {
    submissionSettings,
    updateSettings,
  } = useFormSubmissionSettings();

  const {
    formId,
    effectiveViewMode,
    storageKey,
    getProviderConfig,
    updateProviderConfig,
  } = useFormIntegrationConfigs();

  const [checkingStatus, setCheckingStatus] = useState(false);
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const isDisplayTableAction = actionType === DISPLAY_TABLE_ACTION;
  const isDisplayCardAction = actionType === DISPLAY_CARD_ACTION;

  const eligibleDestinationFields = useMemo(() => {
    if (!isDisplayTableAction) {
      return [];
    }

    return buildEligibleDisplayTableDestinationFields(fieldsMetadata);
  }, [
    fieldsMetadata,
    isDisplayTableAction,
  ]);

  const stagedBitlyConfig = getProviderConfig('bitly');

  const stagedOutputAction =
    stagedBitlyConfig?.outputAction || DISPLAY_CARD_ACTION;

  const stagedBitlyConfigForAction =
    stagedOutputAction === actionType
      ? stagedBitlyConfig
      : null;

  const hasStagedBitlyConfig =
    stagedBitlyConfigForAction &&
    typeof stagedBitlyConfigForAction.enabled === 'boolean';

  const actionSettings = submissionSettings?.[actionType] || {};
  const hasLocalBitlyFlag =
    typeof actionSettings.enableBitly === 'boolean';

  const requestedEnabled = hasStagedBitlyConfig ?
    stagedBitlyConfigForAction.enabled === true :
    hasLocalBitlyFlag ?
      actionSettings.enableBitly === true :
      false;

  const selectedDestinationUfi =
    isDisplayTableAction
      ? (
          stagedBitlyConfigForAction?.destinationUfi ||
          actionSettings?.shortlinkDestination?.destinationUfi ||
          actionSettings?.bitlyDestinationUfi ||
          ''
        )
      : null;

  const selectedDestinationField = useMemo(() => {
    if (!selectedDestinationUfi) {
      return null;
    }

    return eligibleDestinationFields.find(
      (field) => field.ufi === selectedDestinationUfi
    ) || null;
  }, [
    eligibleDestinationFields,
    selectedDestinationUfi,
  ]);

  const hasDestinationField =
    !isDisplayTableAction ||
    Boolean(selectedDestinationUfi);

  const isConnected =
    integrationStatus?.isConnected === true ||
    integrationStatus?.status === 'connected' ||
    integrationStatus?.status === 'active';

  const activeEnabled =
    isConnected &&
    requestedEnabled &&
    hasDestinationField;

  const providerLogin = isConnected ?
    integrationStatus?.providerAccount?.login || null :
    null;

  const tooltipText =
    isDisplayTableAction
      ? 'Create a Bitly shortlink from a selected Display Table URL field.'
      : `Create a Bitly shortlink from the ${generatedOutputLabel}.`;

  const canManageIntegrations = useMemo(() => {
    const role = userRole || userProfile?.role || '';

    return Boolean(
      userProfile?.isOwner ||
        ADMIN_ROLES.has(role) ||
        hasPermission?.('manageIntegrations') ||
        hasPermission?.('manageOrganization')
    );
  }, [
    hasPermission,
    userProfile,
    userRole,
  ]);

  const refreshStatus = useCallback(async () => {
    if (!organization?.id) {
      setIntegrationStatus(null);
      setErrorMsg('');
      return;
    }

    setCheckingStatus(true);
    setErrorMsg('');

    try {
      const status = await fetchIntegrationStatus({
        organizationId: organization.id,
        provider: 'bitly',
      });

      setIntegrationStatus(status);
    } catch (error) {
      console.error(
        '[BitlyShortlinkSettingsSection] Status check failed:',
        error
      );
      setErrorMsg(getCallableErrorMessage(error));
      setIntegrationStatus(null);
    } finally {
      setCheckingStatus(false);
    }
  }, [organization?.id]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const handleManageBitlyClick = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const integrationsUrl = `${window.location.origin}/integrations`;

    const opened = window.open(
        integrationsUrl,
        '_blank',
        'noopener,noreferrer',
    );

    if (!opened) {
      setErrorMsg(
          'Your browser blocked the Integrations tab. Allow pop-ups ' +
          'or open Integrations from the main navigation.',
      );
    }
  }, []);

  const writeActionBitlySettings = useCallback(({
    enabled,
    destinationUfiOverride = null,
  }) => {
    const existingDisplay = actionSettings.shortlinkDisplay || {};
    const existingDestination = actionSettings.shortlinkDestination || {};

    const resolvedDestinationUfi =
      isDisplayTableAction
        ? (
            asStringOrNull(destinationUfiOverride) ||
            asStringOrNull(selectedDestinationUfi) ||
            null
          )
        : null;

    const basePatch = {
      enableBitly: enabled,
      shortlinkProvider: enabled ? 'bitly' : null,
      shortlinkDisplay: {
        ...existingDisplay,
        provider: 'bitly',
        show: enabled,
        label: existingDisplay.label || 'Short URL',
        copyEnabled:
          existingDisplay.copyEnabled ??
          actionSettings.copyIconEnabled ??
          true,
      },
    };

    if (!isDisplayTableAction) {
      updateSettings(actionType, basePatch);
      return;
    }

    const currentColumnOrder = Array.isArray(actionSettings.columnOrder)
      ? actionSettings.columnOrder
      : [];

    const nextColumnOrder = enabled
      ? upsertProviderColumn(
          upsertProviderColumn(
            currentColumnOrder,
            buildBitlyShortUrlColumn({
              sourceUfi: resolvedDestinationUfi,
              visible: true,
              order: currentColumnOrder.length,
            }),
            {
              // Existing behavior:
              // When the user enables Bitly for Display Table, the generated
              // Bitly Short URL column should appear immediately.
              preserveExistingOrder: true,
              preserveExistingVisibility: false,
            }
          ),
          buildBitlyClicksTotalColumn({
            // Enterprise UX:
            // - Register the Bitly Clicks metric column so it becomes available
            //   in Manage Columns.
            // - Keep it hidden by default so enabling Bitly does not suddenly
            //   add a metrics column to existing submissions table layouts.
            // - If an admin has already chosen to show/hide/reorder this
            //   column, preserve that choice on future Bitly setting edits.
            visible: false,
            order: currentColumnOrder.length + 1,
          }),
          {
            preserveExistingOrder: true,
            preserveExistingVisibility: true,
          }
        )
      : hideProviderColumn(
          hideProviderColumn(
            currentColumnOrder,
            BITLY_SHORT_URL_COLUMN_ID
          ),
          BITLY_CLICKS_TOTAL_COLUMN_ID
        );

    updateSettings(actionType, {
      ...basePatch,
      shortlinkDestination: {
        ...existingDestination,
        provider: 'bitly',
        destinationUrlSource: 'responseUfi',
        destinationUfi: resolvedDestinationUfi,
      },
      bitlyDestinationUfi: resolvedDestinationUfi,
      columnOrder: nextColumnOrder,
    });
  }, [
    actionSettings,
    actionType,
    isDisplayTableAction,
    selectedDestinationUfi,
    updateSettings,
  ]);

  const stageBitlyConfig = useCallback(({
    enabled,
    destinationUfiOverride = null,
  }) => {
    const destinationUfi =
      isDisplayTableAction
        ? (
            asStringOrNull(destinationUfiOverride) ||
            asStringOrNull(selectedDestinationUfi) ||
            null
          )
        : null;

    const nextConfig = buildBitlyFormConfig({
      enabled,
      actionType,
      destinationUfi,
    });

    const stagedConfig = updateProviderConfig('bitly', nextConfig);

    console.log('[BitlyShortlinkSettingsSection] Staged Bitly config:', {
      formId,
      effectiveViewMode,
      storageKey,
      stagedConfig,
    });

    return stagedConfig;
  }, [
    actionType,
    effectiveViewMode,
    formId,
    isDisplayTableAction,
    selectedDestinationUfi,
    storageKey,
    updateProviderConfig,
  ]);

  const handleDestinationChange = useCallback((event) => {
    const nextDestinationUfi = event.target.value || '';

    console.log('[BitlyShortlinkSettingsSection] Destination changed:', {
      actionType,
      formId,
      effectiveViewMode,
      nextDestinationUfi,
      requestedEnabled,
    });

    setErrorMsg('');

    writeActionBitlySettings({
      enabled: requestedEnabled,
      destinationUfiOverride: nextDestinationUfi,
    });

    if (requestedEnabled) {
      stageBitlyConfig({
        enabled: true,
        destinationUfiOverride: nextDestinationUfi,
      });
    }
  }, [
    actionType,
    effectiveViewMode,
    formId,
    requestedEnabled,
    stageBitlyConfig,
    writeActionBitlySettings,
  ]);

  useEffect(() => {
    if (!isDisplayTableAction) return;
    if (selectedDestinationUfi) return;
    if (eligibleDestinationFields.length !== 1) return;

    const onlyField = eligibleDestinationFields[0];

    console.log('[BitlyShortlinkSettingsSection] Auto-selected Display Table Bitly destination:', {
      actionType,
      formId,
      effectiveViewMode,
      destinationUfi: onlyField.ufi,
      destinationLabel: onlyField.label,
    });

    writeActionBitlySettings({
      enabled: requestedEnabled,
      destinationUfiOverride: onlyField.ufi,
    });

    if (requestedEnabled) {
      stageBitlyConfig({
        enabled: true,
        destinationUfiOverride: onlyField.ufi,
      });
    }
  }, [
    actionType,
    effectiveViewMode,
    eligibleDestinationFields,
    formId,
    isDisplayTableAction,
    requestedEnabled,
    selectedDestinationUfi,
    stageBitlyConfig,
    writeActionBitlySettings,
  ]);

  const handleToggleBitly = useCallback((event) => {
    const nextEnabled = event.target.checked;

    console.log('[BitlyShortlinkSettingsSection] Toggle changed:', {
      actionType,
      nextEnabled,
      isConnected,
      currentStatus: integrationStatus?.status || null,
      formId,
      effectiveViewMode,
      selectedDestinationUfi,
    });

    if (nextEnabled && !isConnected) {
      setErrorMsg('Connect Bitly before enabling short URLs.');
      return;
    }

    if (
      nextEnabled &&
      isDisplayTableAction &&
      !selectedDestinationUfi
    ) {
      setErrorMsg('Select the Display Table URL field to shorten before enabling Bitly.');
      return;
    }

    setErrorMsg('');
    writeActionBitlySettings({
      enabled: nextEnabled,
      destinationUfiOverride: selectedDestinationUfi,
    });
    stageBitlyConfig({
      enabled: nextEnabled,
      destinationUfiOverride: selectedDestinationUfi,
    });
  }, [
    actionType,
    effectiveViewMode,
    formId,
    integrationStatus?.status,
    isConnected,
    isDisplayTableAction,
    selectedDestinationUfi,
    stageBitlyConfig,
    writeActionBitlySettings,
  ]);

  const handleDisableSavedSetting = useCallback(() => {
    console.log(
      '[BitlyShortlinkSettingsSection] Clearing staged Bitly setting:',
      {
        actionType,
        currentStatus: integrationStatus?.status || null,
        formId,
        effectiveViewMode,
      }
    );

    setErrorMsg('');
    writeActionBitlySettings({
      enabled: false,
      destinationUfiOverride: selectedDestinationUfi,
    });
    stageBitlyConfig({
      enabled: false,
      destinationUfiOverride: selectedDestinationUfi,
    });
  }, [
    actionType,
    effectiveViewMode,
    formId,
    integrationStatus?.status,
    selectedDestinationUfi,
    stageBitlyConfig,
    writeActionBitlySettings,
  ]);

  const renderLoadingState = () => (
    <Stack direction="row" spacing={1} alignItems="center">
      <CircularProgress size={18} />
      <Typography variant="body2" color="text.secondary">
        Checking Bitly status...
      </Typography>
    </Stack>
  );

  const renderDestinationSelector = () => {
    if (!isDisplayTableAction) {
      return null;
    }

    const hasOptions = eligibleDestinationFields.length > 0;

    return (
      <Stack spacing={1}>
        <FormControl
          size="small"
          fullWidth
          disabled={!hasOptions}
        >
          <InputLabel id="bitly-display-table-destination-label">
            URL field to shorten
          </InputLabel>

          <Select
            labelId="bitly-display-table-destination-label"
            label="URL field to shorten"
            value={selectedDestinationUfi || ''}
            onChange={handleDestinationChange}
          >
            {eligibleDestinationFields.map((field) => (
              <MenuItem key={field.ufi} value={field.ufi}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ minWidth: 0 }}
                >
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ maxWidth: 220 }}
                  >
                    {field.label}
                  </Typography>

                  <Chip
                    size="small"
                    label={getFieldTypeLabel(field.type)}
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.6875rem' }}
                  />
                </Stack>
              </MenuItem>
            ))}

            {selectedDestinationUfi && !selectedDestinationField && (
              <MenuItem value={selectedDestinationUfi}>
                Selected field not found ({selectedDestinationUfi})
              </MenuItem>
            )}
          </Select>
        </FormControl>

        {!hasOptions ? (
          <Alert
            severity="info"
            sx={{
              alignItems: 'center',
              '& .MuiAlert-message': {
                py: 0.25,
                fontSize: '0.875rem',
              },
            }}
          >
            Create a calculated URL field or URL field before enabling Bitly
            for Display Table.
          </Alert>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Select the field that contains the generated URL you want Bitly to shorten.
            Calculated URL fields are recommended.
          </Typography>
        )}
      </Stack>
    );
  };

  const renderDisconnectedState = () => (
    <Stack spacing={1.25}>
      <Alert
        severity="warning"
        sx={{
          alignItems: 'center',
          '& .MuiAlert-message': {
            py: 0.25,
            fontSize: '0.875rem',
            fontWeight: 500,
          },
        }}
      >
        Disconnected
      </Alert>

      <Button
        size="small"
        variant="outlined"
        onClick={handleManageBitlyClick}
        endIcon={<OpenInNewIcon />}
        sx={{
          alignSelf: 'flex-start',
          textTransform: 'none',
        }}
      >
        {canManageIntegrations ? 'Connect Bitly' : 'View integrations'}
      </Button>

      {requestedEnabled && (
        <Alert
          severity="info"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleDisableSavedSetting}
            >
              Disable
            </Button>
          }
          sx={{
            alignItems: 'center',
            '& .MuiAlert-message': {
              py: 0.25,
            },
          }}
        >
          Staged short URL setting is inactive.
        </Alert>
      )}
    </Stack>
  );

  const renderConnectedState = () => (
    <Stack spacing={1.5}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
      >
        <Chip
          size="small"
          label="Connected"
          color="success"
          variant="filled"
        />

        {providerLogin && (
          <Typography variant="caption" color="text.secondary">
            Connected as {providerLogin}
          </Typography>
        )}
      </Stack>

      <FormControlLabel
        control={
          <Switch
            checked={activeEnabled}
            onChange={handleToggleBitly}
            size="small"
          />
        }
        label={
          <Box>
            <Typography variant="body2">
              Enable Short URL for this form
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isDisplayTableAction
                ? 'Show a Bitly shortlink column after the selected table URL field is submitted.'
                : 'Show a Bitly shortlink after the generated URL is submitted.'}
            </Typography>
          </Box>
        }
        sx={{
          alignItems: 'flex-start',
          ml: 0,
        }}
      />

      <Button
        size="small"
        variant="text"
        onClick={handleManageBitlyClick}
        endIcon={<OpenInNewIcon />}
        sx={{
          alignSelf: 'flex-start',
          textTransform: 'none',
          px: 0,
        }}
      >
        Manage Bitly
      </Button>
    </Stack>
  );

  const renderStatusContent = () => {
    if (checkingStatus) {
      return renderLoadingState();
    }

    if (!isConnected) {
      return renderDisconnectedState();
    }

    return renderConnectedState();
  };

  return (
    <Stack spacing={1.5}>
      <Box>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <LinkIcon fontSize="small" color="primary" />

          <Typography variant="subtitle2" fontWeight={700}>
            Short URL
          </Typography>

          <Tooltip title={tooltipText} arrow placement="top">
            <IconButton
              size="small"
              aria-label="Short URL information"
              sx={{
                p: 0.25,
                color: 'text.secondary',
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {isDisplayCardAction && (
        <Typography variant="caption" color="text.secondary">
          Bitly will shorten the Display Card generated URL after submission.
        </Typography>
      )}

      {renderDestinationSelector()}

      {errorMsg && (
        <Alert
          severity="error"
          sx={{
            alignItems: 'center',
            '& .MuiAlert-message': {
              py: 0.25,
            },
          }}
        >
          {errorMsg}
        </Alert>
      )}

      {renderStatusContent()}

      <Button
        size="small"
        variant="text"
        onClick={refreshStatus}
        disabled={checkingStatus}
        startIcon={<RefreshIcon />}
        sx={{
          alignSelf: 'flex-start',
          textTransform: 'none',
          px: 0,
        }}
      >
        Refresh status
      </Button>
    </Stack>
  );
}